/**
 * User capability detection utilities for observatory administration
 * Determines if users have admin capabilities on observatories
 */

/**
 * Check if a user has observatory admin capabilities based on their memberships
 * @param {Array} memberships - Array of user's observatory memberships
 * @returns {boolean} - True if user has admin capabilities on any observatory
 */
export const hasObservatoryAdminCapabilities = (memberships) => {
  if (!memberships || !Array.isArray(memberships) || memberships.length === 0) {
    return false;
  }

  return memberships.some(membership => {
    // Check modern permissions structure first
    if (membership.permissions) {
      // User has admin capabilities if they can admin OR are owner
      return membership.permissions.can_admin || membership.is_owner;
    }
    
    // Fall back to legacy role-based system
    if (membership.role) {
      // Owner and admin roles have administrative capabilities
      return ['owner', 'admin'].includes(membership.role);
    }
    
    // No admin capabilities found
    return false;
  });
};

/**
 * Get list of observatories where user has admin capabilities
 * @param {Array} memberships - Array of user's observatory memberships
 * @returns {Array} - Array of observatories user can administer
 */
export const getAdministrableObservatories = (memberships) => {
  if (!memberships || !Array.isArray(memberships)) {
    return [];
  }

  return memberships.filter(membership => {
    // Check modern permissions structure first
    if (membership.permissions) {
      return membership.permissions.can_admin || membership.is_owner;
    }
    
    // Fall back to legacy role-based system
    if (membership.role) {
      return ['owner', 'admin'].includes(membership.role);
    }
    
    return false;
  });
};

/**
 * Check if user can administer a specific observatory
 * @param {Array} memberships - Array of user's observatory memberships
 * @param {string} observatoryId - ID of the observatory to check
 * @returns {boolean} - True if user can administer this observatory
 */
export const canAdministerObservatory = (memberships, observatoryId) => {
  if (!memberships || !Array.isArray(memberships) || !observatoryId) {
    return false;
  }

  const membership = memberships.find(m => 
    m.observatory_id === observatoryId || 
    m._id === observatoryId
  );

  if (!membership) {
    return false;
  }

  // Check modern permissions structure first
  if (membership.permissions) {
    return membership.permissions.can_admin || membership.is_owner;
  }
  
  // Fall back to legacy role-based system
  if (membership.role) {
    return ['owner', 'admin'].includes(membership.role);
  }
  
  return false;
};

/**
 * Check if user can view a specific observatory (matches the server's
 * can_view requirement, with the usual hierarchy where any higher permission
 * implies view). Server-admin status is handled by callers.
 * @param {Array} memberships - Array of user's observatory memberships
 * @param {string} observatoryId - ID of the observatory to check
 * @returns {boolean} - True if the user has view access to this observatory
 */
export const canViewObservatory = (memberships, observatoryId) => {
  if (!memberships || !Array.isArray(memberships) || !observatoryId) {
    return false;
  }

  const membership = memberships.find(m =>
    m.observatory_id === observatoryId ||
    m._id === observatoryId
  );

  if (!membership) {
    return false;
  }

  // Modern permissions: any granted permission (or ownership) implies view.
  if (membership.permissions) {
    const p = membership.permissions;
    return !!(p.can_view || p.can_observe || p.can_operate || p.can_admin || membership.is_owner);
  }

  // Legacy role-based system — every assigned role can at least view.
  if (membership.role) {
    return ['viewer', 'observer', 'operator', 'manager', 'admin', 'owner'].includes(membership.role);
  }

  return !!membership.is_owner;
};

/**
 * Determine what type of admin interface user should see
 * @param {Object} user - User object with role information
 * @param {Array} memberships - Array of user's observatory memberships
 * @returns {Object} - Object indicating which admin interfaces to show
 */
export const getAdminInterfaceConfig = (user, memberships, orgMemberships) => {
  const isServerAdmin = user?.role === 'server_admin';
  const hasObservatoryAdmin = hasObservatoryAdminCapabilities(memberships);
  // orgMemberships comes from getUserOrganizations() which returns
  // { organization: {...}, is_owner: boolean, permissions: {...} }
  const isOrgOwner = Array.isArray(orgMemberships) && orgMemberships.some(org => org.is_owner);
  const isOrgAdmin = Array.isArray(orgMemberships) && orgMemberships.some(org =>
    org.is_owner || org.permissions?.can_manage_members || org.permissions?.can_manage_observatories
  );
  const hasAdminCapabilities = hasObservatoryAdmin || isOrgOwner || isOrgAdmin;

  return {
    showAdminMenu: isServerAdmin || hasAdminCapabilities,  // Show admin menu for observatory/org admins too
    isFullAdmin: isServerAdmin,                             // Only server admins see all items
    showAdministrativeMenu: !isServerAdmin && hasAdminCapabilities, // Legacy — kept for compatibility
    showNoAdminInterface: !isServerAdmin && !hasAdminCapabilities,
    isOrgOwner,
    isOrgAdmin,
    userType: isServerAdmin ? 'server_admin' :
              hasObservatoryAdmin ? 'observatory_admin' :
              isOrgOwner ? 'org_owner' :
              isOrgAdmin ? 'org_admin' : 'regular_user'
  };
};

/**
 * Legacy compatibility: Check if user has the old admin roles
 * @param {Object} user - User object with role information  
 * @returns {boolean} - True if user has legacy admin roles
 */
export const hasLegacyAdminRole = (user) => {
  return user?.role === 'server_admin' || user?.role === 'admin' || user?.role === 'instructor';
};